import { ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'

import { faCheck } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { useSetState } from '@domain-group/react-hooks'

import { Box, Button, Columns } from '@domain-group/suit-theme'

import { getQueryParamsFromHash, parsePermittedQueryParams } from '@domain-group/javascript-utils'

import useFlipperKey from '@hooks/useFlipperKey'
import useInvoice from '@hooks/useInvoice'

import PageHeader from '@components/PageHeader'

import useCurrentUser from '@hooks/useCurrentUser'
import generateTabBarItems from '../../utils/generateTabBarItems'

interface PageLayoutProps {
  activeTabBarItemKey?: string,
  children: ReactNode,
  invoicePayload: ReturnType<typeof useInvoice>,
}

const defaultState = {
  updating: false,
}

const PageLayout = (props: PageLayoutProps) => {
  const {
    activeTabBarItemKey,
    invoicePayload: {
      callbacks: { approveInvoice: approveFn, handleCallbackActionWithConfirmation },
      invoice,
      urls: { invoicesIndexUrl },
    },
    children,
  } = props

  const [state, setState] = useSetState(defaultState)
  const { updating } = state

  const { redirect }: {redirect?: string} = parsePermittedQueryParams(getQueryParamsFromHash(), ['redirect'])

  const { isProviderUser } = useCurrentUser()
  const canViewApproveButton = !!isProviderUser

  const { approved, void: isVoid } = invoice
  const shouldShowApproveButton = canViewApproveButton && !approved && !isVoid

  const approveInvoicePayload = {
    callbacks: {
      actionFn: approveFn,
      afterAction: () => setState({ updating: false }),
      afterError: () => setState({ updating: false }),
      beforeAction: () => setState({ updating: true }),
    },
    strings: {
      actionString: 'approve',
    },
  }

  return (
    <>
      <Helmet>
        <title>{`Invoice Details ${invoice.id} | Realhub`}</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <>
            <Button
              // @ts-expect-error TS2322
              as="a"
              buttonStyle="secondaryUtility"
              href={redirect || invoicesIndexUrl}
              size="medium"
              width="auto"
            >
              Back
            </Button>

            {shouldShowApproveButton
            && (
              <Button
                buttonStyle="primaryEdit"
                icon={<FontAwesomeIcon icon={faCheck} />}
                loading={updating}
                onClick={() => {
                  // @ts-expect-error TS2345
                  handleCallbackActionWithConfirmation(approveInvoicePayload)
                }}
                size="medium"
                // @ts-expect-error TS2322
                marginLeft="medium"
                width={['100%', 'auto']}
              >
                {approved ? 'Approved' : 'Approve'}
              </Button>
            )}
          </>
        )}
        activeTabBarItemKey={activeTabBarItemKey}
        tabBarItems={generateTabBarItems(invoice)}
        title={`Invoice Details #${invoice.id}`}
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          {children}
        </Columns>
      </Box>
    </>
  )
}
export default PageLayout
